import React, { memo, FC } from "react";
import {
  Paper,
  Grow,
  Typography,
  TableRow,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
} from "@mui/material";
import Choices from "../Choices";
import ReusableAccordian from "../ReusableAccordion";
import { Image } from "cloudinary-react";
import { tier1, tier2, tier3, tier4, tier5 } from "./defaults";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useWoodSelector from "./useWoodSelector";
import PleaseNote from "../PleaseNote";

const StyledSpan = styled("span")({
  color: "red",
  fontSize: 22,
});

const baseWoods = [
  "Paint-Grade Bass",
  "Stain-Grade Bass",
  "Paint-Grade Douglas Fir",
  "Paint-Grade Pine",
  "Stain-Grade Pine",
];

interface UpchargeWoodsProps {
  label: string;
}

interface TierItm {
  id: number;
  type: string;
  label: string;
  abbrev: string;
  material: string;
}

interface RenderTierProps {
  tier: TierItm[];
  handleClick: (label: string, abbrev: string) => void;
  chipMaterial: string;
  variant: (label: string) => "filled" | "outlined";
  checkMark: (label: string) => boolean;
}

const UpchargeWoods: FC<UpchargeWoodsProps> = ({ label }) => {
  return (
    <Typography variant="body2" style={{ fontSize: "0.8125rem" }}>
      {label} <span>*</span>
    </Typography>
  );
};

const RenderTier: FC<RenderTierProps> = ({
  tier,
  handleClick,
  chipMaterial,
  variant,
  checkMark,
}) => {
  const tierOptions = tier.filter(
    (wood) => wood.material === chipMaterial || wood.material === "both"
  );

  if (!tierOptions?.length) {
    return <Typography>No available options</Typography>;
  }

  return (
    <>
      {tierOptions.map((state) => (
        <div
          key={state.id}
          onClick={() => handleClick(state.label, state.abbrev)}
        >
          <Choices
            label={
              baseWoods?.includes(state.label) ? (
                state.label
              ) : (
                <UpchargeWoods label={state.label} />
              )
            }
            variant={variant(state.label)}
            checkMark={checkMark(state.label)}
          />
        </div>
      ))}
    </>
  );
};

const TierBorder = styled("span")({
  position: "absolute",
  borderRight: "2px solid black",
  right: 0,
  top: "20%",
  width: 5,
  height: "60%",
});

const StyledTableCell = styled(TableCell)({
  "&.MuiTableCell-root": {
    borderBottom: "none",
    borderRight: "2px solid black",
    margin: 5,
    position: "relative",
    fontSize: 18,
  },
});

const StyledTableCell2 = styled(TableCell)({
  "&.MuiTableCell-root": {
    borderBottom: "none",
    margin: 5,
    position: "relative",
    fontSize: 18,
    marginBottom: 10,
    marginTop: 20,
    alignItems: "center",
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
});

const WoodAccordian: FC = () => {
  const {
    chipMaterial,
    variant,
    checkMark,
    handleClick,
    woodObj,
    largeScreen,
    materialChoice,
    doorType,
    woodSpecies,
  } = useWoodSelector();

  var tiers = [
    { tierNum: 1, tier: tier1 },
    { tierNum: 2, tier: tier2 },
    { tierNum: 3, tier: tier3 },
    { tierNum: 4, tier: tier4 },
    { tierNum: 5, tier: tier5 },
  ];

  if(materialChoice === "All Wood"){
    tiers.shift()
    tiers = tiers.map(itm => ({ tierNum: itm.tierNum - 1, tier: itm.tier }))
  }

  if (materialChoice === "All Aluminum") return null;

  return (
    <ReusableAccordian
      accordionName="wood"
      heading="Wood Species"
      secondaryHeading="Select wood finish"
      colOrRow="col"
      required5={true}
    >
      {!materialChoice ? (
        <PleaseNote notes="Select a Material to view Wood Species Options" />
      ) : materialChoice === "All Wood" && doorType === "Pivot" ? (
        <>
          <PleaseNote
            notes={[
              "Wood grains may vary; picture may not be exact to wood species",
              "Up-charge applies to items marked with an asterisk (*)",
            ]}
          />

          <ColLayout style={{ flexWrap: "wrap" }}>
            {woodSpecies && woodObj?.imgLabel ? (
              <Grow in={true} timeout={500}>
                <RowLayout style={{ width: 300 }}>
                  <Paper style={{ width: 150, height: 150 }} elevation={3}>
                    <Image
                      cloudName="ag-millworks"
                      secure="true"
                      publicId={`WoodSpecies/${woodObj.imgLabel}`}
                    />
                  </Paper>
                </RowLayout>
              </Grow>
            ) : (
              <div style={{ width: 300 }} />
            )}

            <RowLayout>
              {tier5
                .filter((itm) => itm.label?.includes("Accoya"))
                .map((state) => (
                  <div
                    key={state.id}
                    onClick={() => handleClick(state.label, state.abbrev)}
                  >
                    <Choices
                      label={
                        baseWoods?.includes(state.label) ? (
                          state.label
                        ) : (
                          <UpchargeWoods label={state.label} />
                        )
                      }
                      variant={variant(state.label)}
                      checkMark={checkMark(state.label)}
                    />
                  </div>
                ))}
            </RowLayout>
          </ColLayout>
        </>
      ) : (
        <>
          <PleaseNote
            notes={[
              "Wood grains may vary; picture may not be exact to wood species",
              materialChoice?.includes("Clad") && "If Paint Grade is required, we strongly recommend PG-Basswood",
              "Upcharge increases per category"
            ]}
          />

          <div
            style={{
              marginBottom: 10,
              marginTop: 20,
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
              width: "100%",
              flexDirection: largeScreen ? "row" : "column",
            }}
          >
            {woodSpecies && woodObj?.imgLabel ? (
              <Grow in={true} timeout={500}>
                <RowLayout style={{ width: 300 }}>
                  <Paper style={{ width: 150, height: 150 }} elevation={3}>
                    <Image
                      cloudName="ag-millworks"
                      secure="true"
                      publicId={`WoodSpecies/${woodObj.imgLabel}`}
                    />
                  </Paper>
                </RowLayout>
              </Grow>
            ) : (
              <div style={{ width: 300 }} />
            )}

            <TableContainer style={{ flexBasis: "50%" }}>
              <Table size="small">
                <TableBody>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding="none"
                        style={{
                          fontWeight: "bold",
                          borderBottom: "none",
                          paddingLeft: 10,
                        }}
                        align="center"
                      >
                        Category
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  {tiers.map((itm) => (
                    <TableRow key={itm.tierNum} style={{ minWidth: 750 }}>
                      <StyledTableCell
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {itm.tierNum}
                        <StyledSpan />
                      </StyledTableCell>
                      <StyledTableCell2 align="right">
                        <RenderTier
                          tier={itm.tier}
                          handleClick={handleClick}
                          chipMaterial={chipMaterial}
                          variant={variant}
                          checkMark={checkMark}
                        />
                      </StyledTableCell2>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </>
      )}
    </ReusableAccordian>
  );
};

export default memo(WoodAccordian);
